import { Outlet, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Sidebar from './Sidebar';
import { LayoutDashboard, BookOpen, Users, MessageSquare, UserPlus, LogOut, Menu, X, Home, GraduationCap, Shield, BarChart3, Users2, FileEdit, ScrollText, HeartHandshake, CreditCard, Database, ClipboardList, Video, History, Activity, Award, Sparkles } from 'lucide-react';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('admin');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems = useMemo(() => [
    { label: t('nav.dashboard'), href: '/admin', icon: LayoutDashboard },
    { label: t('nav.homePage'), href: '/admin/home-page', icon: FileEdit },
    { label: t('nav.timeline'), href: '/admin/timeline', icon: History },
    { label: t('nav.headerFooter'), href: '/admin/navigation', icon: Menu },
    { label: t('nav.courseManagement'), href: '/admin/courses', icon: BookOpen },
    { label: t('nav.trainerManagement'), href: '/admin/trainers', icon: Users2 },
    { label: t('nav.partners'), href: '/admin/partners', icon: HeartHandshake },
    { label: t('nav.users'), href: '/admin/users', icon: Users },
    { label: t('nav.contacts'), href: '/admin/contacts', icon: MessageSquare },
    { label: t('nav.enrollments'), href: '/admin/enrollments', icon: UserPlus },
    { label: t('nav.certifications'), href: '/admin/certifications', icon: Award },
    { label: t('nav.premiumNumbers'), href: '/admin/premium-numbers', icon: Sparkles },
    { label: t('nav.billing'), href: '/admin/billing', icon: CreditCard },
    { label: t('nav.exams'), href: '/admin/exams', icon: ClipboardList },
    { label: t('nav.videoProcessing'), href: '/admin/video-jobs', icon: Video },
    { label: t('nav.videoFailures'), href: '/admin/video-monitoring', icon: Activity },
    { label: t('nav.objectManagement'), href: '/admin/object-management', icon: Database },
    { label: t('nav.analytics'), href: '/admin/analytics', icon: BarChart3 },
    { label: t('nav.dataBackup'), href: '/admin/backup', icon: Database },
    { label: t('nav.systemLogs'), href: '/admin/logs', icon: ScrollText },
  ], [t]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Top Bar */}
      <header className="glass-refined border-b border-gray-200/50 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center">
            <button
              className="lg:hidden p-2 mr-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <Link to="/admin" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <span className="text-xl font-bold text-red-600 font-display hidden sm:block">{t('title')}</span>
            </Link>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-500 hidden sm:block">{user?.name}</span>
            <Link
              to="/"
              className="flex items-center px-3 py-1.5 text-gray-600 hover:text-gold bg-gray-100 rounded-lg transition-colors"
              title="Back to Site"
            >
              <Home className="w-4 h-4 mr-1.5" />
              <span className="text-sm">{t('common:nav.site')}</span>
            </Link>
            <Link
              to="/dashboard"
              className="flex items-center px-3 py-1.5 text-gray-600 hover:text-gold bg-gray-100 rounded-lg transition-colors"
            >
              <GraduationCap className="w-4 h-4 mr-1.5" />
              <span className="text-sm hidden sm:inline">{t('common:nav.dashboard')}</span>
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center text-gray-500 hover:text-red-600 transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Desktop Sidebar */}
        <div className="hidden lg:block sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto">
          <Sidebar navItems={navItems} title={t('administration')} />
        </div>

        {/* Mobile Sidebar */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-30 bg-black/30 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)}>
            <div className="w-64 h-full" onClick={(e) => e.stopPropagation()}>
              <Sidebar navItems={navItems} title={t('administration')} />
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 p-6 luxury-bg">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
